/**
 * Provider playback eligibility — distinct from catalog availability.
 *
 * availability: whether YouTube currently exposes the upload (public / private /
 * deleted / …).
 * playbackEligibility: whether Rhythmé may embed and play that upload.
 *
 * Those are not the same: an upload can be available and still embed_blocked.
 * Phase 4B canonical resolution is unrelated and must not be derived here.
 *
 * Availability string values match AVAILABILITY_STATES in schema.js. This
 * module must not import schema.js (builders import this file).
 */

const AVAILABLE = 'available';
const PRIVATE = 'private';
const DELETED = 'deleted';
const REMOVED = 'removed';

export const PLAYBACK_ELIGIBILITY_STATES = Object.freeze({
  ELIGIBLE: 'eligible',
  PRIVATE: 'private',
  DELETED: 'deleted',
  EMBED_BLOCKED: 'embed_blocked',
  UNKNOWN: 'unknown',
});

export const EMBEDDABLE_STATES = Object.freeze({
  TRUE: 'true',
  FALSE: 'false',
  UNKNOWN: 'unknown',
});

export const PLACEHOLDER_PROVIDER_TITLES = Object.freeze([
  'Private video',
  'Deleted video',
]);

const PLACEHOLDER_TITLE_SET = new Set(PLACEHOLDER_PROVIDER_TITLES);
const WRITABLE_ELIGIBILITY_STATES = Object.values(PLAYBACK_ELIGIBILITY_STATES);
const WRITABLE_EMBEDDABLE_STATES = Object.values(EMBEDDABLE_STATES);

function nonemptyString(value) {
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null;
}

export function normalizeEmbeddableState(value) {
  if (value === true || value === EMBEDDABLE_STATES.TRUE) {
    return EMBEDDABLE_STATES.TRUE;
  }
  if (value === false || value === EMBEDDABLE_STATES.FALSE) {
    return EMBEDDABLE_STATES.FALSE;
  }
  return EMBEDDABLE_STATES.UNKNOWN;
}

export function isPlaceholderProviderTitle(title) {
  const text = nonemptyString(title);
  return Boolean(text && PLACEHOLDER_TITLE_SET.has(text));
}

export function isIdentityBearingObservation(observation) {
  const title = nonemptyString(observation?.providerTitle);
  return Boolean(title && !PLACEHOLDER_TITLE_SET.has(title));
}

export function derivePlaybackEligibility({
  availability,
  embeddable,
  checkedAt = null,
  reason = null,
} = {}) {
  const embeddableState = normalizeEmbeddableState(embeddable);
  let state = PLAYBACK_ELIGIBILITY_STATES.UNKNOWN;

  if (availability === PRIVATE) {
    state = PLAYBACK_ELIGIBILITY_STATES.PRIVATE;
  } else if (availability === DELETED) {
    state = PLAYBACK_ELIGIBILITY_STATES.DELETED;
  } else if (embeddableState === EMBEDDABLE_STATES.FALSE) {
    state = PLAYBACK_ELIGIBILITY_STATES.EMBED_BLOCKED;
  } else if (
    availability === AVAILABLE
    && embeddableState === EMBEDDABLE_STATES.TRUE
  ) {
    state = PLAYBACK_ELIGIBILITY_STATES.ELIGIBLE;
  }

  return {
    state,
    embeddable: embeddableState,
    checkedAt: checkedAt ?? null,
    reason: nonemptyString(reason),
  };
}

export function buildPlaybackEligibility(input) {
  if (input === undefined || input === null) {
    return derivePlaybackEligibility();
  }
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    throw new TypeError('playbackEligibility must be an object');
  }

  const eligibility = derivePlaybackEligibility({
    availability: input.availability,
    embeddable: input.embeddable,
    checkedAt: input.checkedAt,
    reason: input.reason,
  });

  if (
    input.state !== undefined
    && input.state !== null
    && !WRITABLE_ELIGIBILITY_STATES.includes(input.state)
  ) {
    throw new RangeError(`Unsupported playbackEligibility.state: ${input.state}`);
  }
  if (
    input.embeddable !== undefined
    && input.embeddable !== null
    && input.embeddable !== true
    && input.embeddable !== false
    && !WRITABLE_EMBEDDABLE_STATES.includes(input.embeddable)
  ) {
    throw new RangeError(
      `Unsupported playbackEligibility.embeddable: ${input.embeddable}`,
    );
  }

  if (WRITABLE_ELIGIBILITY_STATES.includes(input.state)) {
    eligibility.state = input.state;
  }
  return eligibility;
}

export function playbackEligibilityFromSource(source = {}) {
  const nested = source?.playbackEligibility;
  return derivePlaybackEligibility({
    availability: source.availability,
    embeddable: nested?.embeddable ?? source.embeddable,
    checkedAt: nested?.checkedAt ?? source.embeddableCheckedAt ?? null,
    reason: nested?.reason ?? source.playbackEligibilityReason ?? null,
  });
}

export function isKnownEmbedBlocked(source = {}) {
  const eligibility = playbackEligibilityFromSource(source);
  return eligibility.embeddable === EMBEDDABLE_STATES.FALSE
    || eligibility.state === PLAYBACK_ELIGIBILITY_STATES.EMBED_BLOCKED;
}

/**
 * Shared playability rule for library items, sync counts, and seed selection.
 * Unknown / absent embeddable stays compatible with pre-PR1 documents.
 */
export function isPlayableProviderSource(source = {}) {
  const availability = source.availability;
  const assetId = nonemptyString(source.providerAssetId)
    || nonemptyString(source.videoId)
    || nonemptyString(source.id);
  if (!assetId) return false;
  if (availability && availability !== AVAILABLE) {
    return false;
  }
  if (source.removedFromPlaylistAt != null) return false;
  if (availability === REMOVED) return false;
  return !isKnownEmbedBlocked(source);
}

export function resolveLastKnownGoodObservation({
  previousObservation = null,
  previousLastKnownGood = null,
  nextObservation = null,
} = {}) {
  if (isIdentityBearingObservation(nextObservation)) {
    return null;
  }
  if (isIdentityBearingObservation(previousLastKnownGood)) {
    return cloneObservation(previousLastKnownGood);
  }
  if (isIdentityBearingObservation(previousObservation)) {
    return cloneObservation(previousObservation);
  }
  return null;
}

export function toAssetLastKnownGoodObservation(observation, availability) {
  if (!isIdentityBearingObservation(observation)) return null;
  const cloned = cloneObservation(observation);
  return {
    providerTitle: cloned.providerTitle ?? null,
    providerChannelId: cloned.providerChannelId ?? null,
    providerChannelTitle: cloned.providerChannelTitle ?? null,
    providerStructuredArtists: cloned.providerStructuredArtists ?? null,
    providerDurationMs: cloned.providerDurationMs ?? null,
    providerThumbnailUrl: cloned.providerThumbnailUrl ?? null,
    providerPublishedAt: cloned.providerPublishedAt ?? null,
    providerCategoryId: cloned.providerCategoryId ?? null,
    availability: cloned.availability
      || availability
      || AVAILABLE,
    observedAt: cloned.observedAt ?? null,
    observationRole: 'authoritative',
  };
}

function cloneObservation(observation) {
  if (!observation || typeof observation !== 'object') return null;
  const cloned = { ...observation };
  if (Array.isArray(observation.providerStructuredArtists)) {
    cloned.providerStructuredArtists = observation.providerStructuredArtists.map(
      (entry) => (
        entry && typeof entry === 'object' && !Array.isArray(entry)
          ? { ...entry }
          : entry
      ),
    );
  }
  return cloned;
}
